import { ErrorMessage, FieldArray, useFormikContext } from 'formik';
import PropTypes from 'prop-types';

import ErrorIcon from '@mui/icons-material/Error';
import {
  Button,
  FormControl,
  FormControlLabel,
  FormGroup,
  Checkbox as MuiCheckbox,
  Stack,
  Typography,
} from '@mui/material';

/**
 * if you're using this component within the Form component, don't forget to pass type='checkbox-group' or else the form won't know how to handle the value
 */
function CheckboxGroup({
  name,
  options,
  readOnly = false,
  value = [],
  selectAll = false,
  container = null,
  ...props
}) {
  const { errors, setFieldValue } = useFormikContext();
  const isChecked = label => {
    const selectedValue = options.find(option => option.label === label)?.value;
    return value.includes(selectedValue);
  };

  const selectAllOptions = () => {
    const newValues = [...value];
    options.forEach(option => newValues.push(option.value));
    setFieldValue(name, [...new Set(newValues)]);
  };

  const renderCheckboxes = () => (
    <>
      {options.map(option => (
        <div key={option.label}>
          {option.formLabel ? (
            option.formLabel({ checked: isChecked(option.label), readOnly, ...props })
          ) : (
            <FormControlLabel
              key={option.label}
              control={<MuiCheckbox name={option.value} type="checkbox" value={option.value} />}
              label={option.label}
              checked={isChecked(option.label)}
              disabled={readOnly}
              {...props}
            />
          )}
        </div>
      ))}
      {selectAll && (
        <Button
          disabled={readOnly}
          variant="outlined"
          onClick={selectAllOptions}
          sx={{ width: 'fit-content' }}
        >
          Select all
        </Button>
      )}
    </>
  );

  return (
    <Stack alignItems="center" sx={{ width: 'fit-content' }}>
      <FormControl required error={errors[name]} component="fieldset" variant="standard">
        <ErrorMessage name={name}>
          {msg => (
            <Stack direction="row" spacing={1}>
              <ErrorIcon color="error" />
              <Typography color="error">{msg}</Typography>
            </Stack>
          )}
        </ErrorMessage>

        <FieldArray
          name={name}
          render={() => (
            <FormGroup row>
              {container ? (
                <>{container(renderCheckboxes())}</>
              ) : (
                <Stack direction="column" justifyContent="flex-start">
                  {renderCheckboxes()}
                </Stack>
              )}
            </FormGroup>
          )}
        />
      </FormControl>
    </Stack>
  );
}

CheckboxGroup.propTypes = {
  name: PropTypes.string.isRequired,
  options: PropTypes.arrayOf(PropTypes.object).isRequired,
  value: PropTypes.arrayOf(PropTypes.object),
  readOnly: PropTypes.bool,
  selectAll: PropTypes.bool,
  container: PropTypes.func,
};

export default CheckboxGroup;
